import { useCallback, useEffect, useRef, useState } from "react";
import {
  getWhatsAppErrorMessage,
  whatsappService,
  WhatsAppChatMessage,
  WhatsAppEvent,
  WhatsAppMediaFile,
  WhatsAppMediaKind,
} from "@/services/whatsapp";

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 15_000;
const RECONNECT_DELAY_MS = 5_000;
// WAHA tarda un instante en persistir el mensaje enviado antes de devolverlo en el listado
const REFRESH_AFTER_SEND_MS = 1_000;

export type ChatSocketStatus = "connecting" | "connected" | "disconnected";

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Une mensajes por id (los nuevos pisan a los viejos) y los ordena del más antiguo al más reciente
function mergeMessages(
  current: WhatsAppChatMessage[],
  incoming: WhatsAppChatMessage[],
): WhatsAppChatMessage[] {
  const byId = new Map(current.map((m) => [m.id, m]));
  incoming.forEach((m) => byId.set(m.id, { ...byId.get(m.id), ...m }));
  return [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
}

export function useWhatsAppChat(chatId: string | null, enabled: boolean) {
  const [messages, setMessages] = useState<WhatsAppChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingEarlier, setIsLoadingEarlier] = useState(false);
  const [hasEarlier, setHasEarlier] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [socketStatus, setSocketStatus] =
    useState<ChatSocketStatus>("disconnected");

  // Cantidad de mensajes antiguos ya cargados, para paginar hacia atrás
  const loadedCountRef = useRef(0);
  // Ids que identifican este chat en los eventos (@c.us y su @lid equivalente)
  const chatAliasesRef = useRef<Set<string>>(new Set());

  const fetchLatest = useCallback(
    async (mode: "initial" | "refresh" | "silent" = "silent") => {
      if (!chatId) return;
      if (mode === "initial") setIsLoading(true);
      if (mode === "refresh") setIsRefreshing(true);
      try {
        const page = await whatsappService.getChatMessages(chatId, {
          limit: PAGE_SIZE,
          offset: 0,
        });
        setError(null);
        setMessages((prev) => {
          const next = mode === "initial" ? mergeMessages([], page) : mergeMessages(prev, page);
          loadedCountRef.current = next.length;
          return next;
        });
        if (mode === "initial") setHasEarlier(page.length === PAGE_SIZE);
      } catch (e) {
        if (mode !== "silent") {
          setError(getWhatsAppErrorMessage(e, "No se pudieron cargar los mensajes"));
        }
      } finally {
        if (mode === "initial") setIsLoading(false);
        if (mode === "refresh") setIsRefreshing(false);
      }
    },
    [chatId],
  );

  const loadEarlier = useCallback(async () => {
    if (!chatId || isLoadingEarlier || !hasEarlier) return;
    setIsLoadingEarlier(true);
    try {
      const page = await whatsappService.getChatMessages(chatId, {
        limit: PAGE_SIZE,
        offset: loadedCountRef.current,
      });
      if (page.length < PAGE_SIZE) setHasEarlier(false);
      setMessages((prev) => {
        const next = mergeMessages(prev, page);
        loadedCountRef.current = next.length;
        return next;
      });
    } finally {
      setIsLoadingEarlier(false);
    }
  }, [chatId, isLoadingEarlier, hasEarlier]);

  // Carga inicial + resolución del @lid del contacto
  useEffect(() => {
    if (!enabled || !chatId) return;
    setMessages([]);
    setHasEarlier(true);
    setError(null);
    loadedCountRef.current = 0;
    chatAliasesRef.current = new Set([chatId]);

    fetchLatest("initial");
    whatsappService.markChatAsRead(chatId).catch(() => undefined);
    whatsappService
      .getLidByPhone(chatId.split("@")[0])
      .then((result) => {
        if (result?.lid) chatAliasesRef.current.add(result.lid);
      })
      .catch(() => undefined);
  }, [enabled, chatId, fetchLatest]);

  // WebSocket de eventos en vivo; si se cae, se reconecta y mientras tanto se hace polling
  useEffect(() => {
    if (!enabled || !chatId) return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const belongsToChat = (payload: WhatsAppEvent["payload"]) =>
      chatAliasesRef.current.has(payload?.from) ||
      chatAliasesRef.current.has(payload?.to);

    const handleEvent = (data: WhatsAppEvent) => {
      const payload = data?.payload;
      if (!payload || !belongsToChat(payload)) return;

      if (data.event === "message.ack") {
        setMessages((prev) =>
          prev.map((m) => (m.id === payload.id ? { ...m, ack: payload.ack } : m)),
        );
        return;
      }
      // message.any: se trae la última página para incluir el mensaje con su forma completa
      sleep(REFRESH_AFTER_SEND_MS).then(() => {
        if (!disposed) fetchLatest("silent");
      });
    };

    const connect = async () => {
      setSocketStatus("connecting");
      try {
        const url = await whatsappService.getEventsSocketUrl([
          "message.any",
          "message.ack",
        ]);
        if (disposed) return;
        socket = new WebSocket(url);
        socket.onopen = () => setSocketStatus("connected");
        socket.onmessage = (e) => {
          try {
            handleEvent(JSON.parse(e.data));
          } catch {
            // Evento no JSON, se ignora
          }
        };
        socket.onclose = () => {
          setSocketStatus("disconnected");
          if (!disposed) reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
        };
      } catch {
        setSocketStatus("disconnected");
        if (!disposed) reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
      setSocketStatus("disconnected");
    };
  }, [enabled, chatId, fetchLatest]);

  useEffect(() => {
    if (!enabled || !chatId || socketStatus === "connected") return;
    const interval = setInterval(() => fetchLatest("silent"), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, chatId, socketStatus, fetchLatest]);

  const sendText = useCallback(
    async (text: string) => {
      if (!chatId) return;
      whatsappService.markChatAsRead(chatId).catch(() => undefined);
      await whatsappService.sendChatText(chatId, text);
      await sleep(REFRESH_AFTER_SEND_MS);
      await fetchLatest("silent");
    },
    [chatId, fetchLatest],
  );

  const sendMedia = useCallback(
    async (kind: WhatsAppMediaKind, file: WhatsAppMediaFile, caption?: string) => {
      if (!chatId) return;
      await whatsappService.sendChatMedia(kind, chatId, file, caption);
      await sleep(REFRESH_AFTER_SEND_MS);
      await fetchLatest("silent");
    },
    [chatId, fetchLatest],
  );

  return {
    messages,
    isLoading,
    isRefreshing,
    isLoadingEarlier,
    hasEarlier,
    error,
    socketStatus,
    refresh: () => fetchLatest("refresh"),
    loadEarlier,
    sendText,
    sendMedia,
  };
}
